<docs>
Renders a Material Symbols (Rounded) icon by name. Purely presentational: no v-model, no events.
Reuse it for any icon need instead of inlining `<svg>`. Many Zone components take a Zone icon name
(ZoneButton `leadingIcon`, ZonePill `icon`, ZoneSelect rows…): the same name space.

## Notes

- With a `tooltip` the glyph is something to hover, so it gets `cursor-pointer`. Without one it
  shows whatever cursor its parent shows (a button's, a label's).
</docs>

<template>
  <ZoneTooltip :content="tooltip">
    <span
      data-testid="icon"
      :class="iconClasses"
      :style="iconStyle"
      v-bind="$attrs"
    >{{ icon }}</span>
  </ZoneTooltip>
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';
import ICONS from '../../../../../config/zone/material-icons.json';
import ZoneTooltip from './Tooltip.vue';

export type IconName = (typeof ICONS)[number];

const SIZE_MAPPINGS: Record<string, string> = {
  xs: 'text-xs',
  lg: 'text-lg',
  xxl: 'text-2xl',
};

const ALIGN_MAPPINGS: Record<string, string> = {
  baseline: 'align-baseline',
  top: 'align-top',
  middle: 'align-middle',
  bottom: 'align-bottom',
  text_top: 'align-text-top',
  text_bottom: 'align-text-bottom',
  sub: 'align-sub',
  super: 'align-super',
};

const COLOR_OPTIONS = [
  'black', 'white', 'gray', 'red', 'orange', 'yellow', 'green',
  'teal', 'primary', 'blue', 'indigo', 'purple', 'pink', 'current',
] as const;

export type IconColor = (typeof COLOR_OPTIONS)[number];

const SHADE_OPTIONS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

export type IconShade = (typeof SHADE_OPTIONS)[number];
</script>

<script setup lang="ts">
// ZoneTooltip is the root, so class/aria-* from the caller are bound onto the glyph by hand: they
// belong on the <span>, not on the tooltip wrapper (and the wrapper is absent without a tooltip).
defineOptions({ name: 'ZoneIcon', inheritAttrs: false });

const props = defineProps({
  /**
   * A Material Symbols name, snake_case (`search`, `expand_more`, `keyboard_arrow_down`). Validated
   * against config/zone/material-icons.json.
   */
  icon: {
    type: String as PropType<IconName>,
    required: true,
    validator: (v: string) => (ICONS as string[]).includes(v),
  },
  /** Only these three sizes exist. */
  size: {
    type: String as PropType<'xs' | 'lg' | 'xxl'>,
    default: 'lg',
    validator: (v: string) => Object.keys(SIZE_MAPPINGS).includes(v),
  },
  /** Vertical alignment of the glyph against the surrounding text. */
  align: {
    type: String as PropType<keyof typeof ALIGN_MAPPINGS>,
    default: null,
    validator: (v: string) => Object.keys(ALIGN_MAPPINGS).includes(v),
  },
  /** `current` inherits the text colour. */
  color: {
    type: String as PropType<IconColor>,
    default: 'current',
    validator: (v: string) => (COLOR_OPTIONS as readonly string[]).includes(v),
  },
  /** Ignored for `current`, `black` and `white`. */
  shade: {
    type: Number as PropType<IconShade>,
    default: 500,
    validator: (v: number) => (SHADE_OPTIONS as readonly number[]).includes(v),
  },
  /** Filled glyph instead of the outlined one. */
  fill: {
    type: Boolean,
    default: false,
  },
  /**
   * Wraps the icon in a hover tooltip (ZoneTooltip). Text only, there is no slot. Omitted or empty
   * renders a bare `<span>`, no wrapper at all.
   */
  tooltip: {
    type: String,
    default: null,
  },
});

const iconClasses = computed<string>(() => {
  const classes: string[] = ['zone-icon', 'font-material-symbols-rounded', 'notranslate'];

  classes.push(SIZE_MAPPINGS[props.size] ?? SIZE_MAPPINGS.lg);

  const { color } = props;

  if (color === 'black' || color === 'white' || color === 'current') {
    classes.push(`text-${color}`);
  } else if (color) {
    classes.push(`text-${color}-${props.shade}`);
  }

  if (props.align) {
    const alignClass = ALIGN_MAPPINGS[props.align];

    if (alignClass) {classes.push(alignClass);}
  }

  // A glyph with a tooltip invites the hover; without one the cursor stays the parent's.
  if (props.tooltip) {classes.push('cursor-pointer');}

  return classes.join(' ');
});

const iconStyle = computed<string>(() => `font-variation-settings: 'FILL' ${props.fill ? 1 : 0};`);
</script>
